import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";
import { Button } from "./Button";

const NAV_LINKS = [
    { label: "Meet Klair", href: "#klair" },
    { label: "Why Klareet", href: "#why" },
    { label: "View Snapshot", to: "/returning" },
];

export function MarketingHeader() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        function onScroll() {
            setScrolled(window.scrollY > 12);
        }
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Lock page scroll when the mobile sheet is open
    useEffect(() => {
        if (open) {
            const prev = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            return () => {
                document.body.style.overflow = prev;
            };
        }
        return undefined;
    }, [open]);

    function smoothScrollTo(hash) {
        const el = document.querySelector(hash);
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        setOpen(false);
    }

    return (
        <>
            <header
                className={`sticky top-0 z-50 border-b border-klareet-bor backdrop-blur-md transition-colors duration-300 ${
                    scrolled
                        ? "bg-klareet-NAVY/85"
                        : "bg-klareet-NAVY/60"
                }`}
                data-testid="marketing-header"
            >
                <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <Logo className="h-10 md:h-12 w-auto" />
                    </Link>

                    <nav className="hidden md:flex items-center gap-2">
                        {NAV_LINKS.map((link) =>
                            link.to ? (
                                <Link
                                    key={link.to}
                                    to={link.to}
                                    className="px-4 py-2 rounded-full text-sm text-klareet-MUT hover:text-klareet-T2 transition-colors"
                                    data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    {link.label}
                                </Link>
                            ) : (
                                <a
                                    key={link.href}
                                    href={link.href}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        smoothScrollTo(link.href);
                                    }}
                                    className="px-4 py-2 rounded-full text-sm text-klareet-MUT hover:text-klareet-T2 transition-colors"
                                    data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    {link.label}
                                </a>
                            ),
                        )}
                        <Button
                            size="sm"
                            onClick={() => navigate("/survey")}
                            data-testid="nav-take-survey"
                        >
                            Take the Survey
                            <ArrowRight size={14} />
                        </Button>
                    </nav>

                    {/* Mobile hamburger */}
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-klareet-bor text-klareet-TEXT"
                        aria-label="Open menu"
                        data-testid="nav-hamburger"
                    >
                        <Menu size={20} />
                    </button>
                </div>
            </header>

            {/* Mobile fullscreen sheet */}
            <AnimatePresence>
                {open ? (
                    <motion.div
                        key="mobile-sheet"
                        initial={{ opacity: 0, y: -16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -16 }}
                        transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
                        className="md:hidden fixed inset-0 z-[60] bg-klareet-NAVY"
                        data-testid="mobile-nav-sheet"
                    >
                        <div className="px-6 sm:px-8 h-20 flex items-center justify-between border-b border-klareet-bor">
                            <Logo className="h-10 w-auto" />
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-klareet-bor text-klareet-TEXT"
                                aria-label="Close menu"
                                data-testid="nav-close"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="px-6 sm:px-8 pt-12 pb-10 space-y-3">
                            {NAV_LINKS.map((link, i) =>
                                link.to ? (
                                    <motion.div
                                        key={link.to}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.06 + i * 0.06 }}
                                    >
                                        <Link
                                            to={link.to}
                                            onClick={() => setOpen(false)}
                                            className="block py-4 text-2xl font-semibold tracking-tight text-klareet-TEXT border-b border-klareet-bor"
                                            data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}-mobile`}
                                        >
                                            {link.label}
                                        </Link>
                                    </motion.div>
                                ) : (
                                    <motion.a
                                        key={link.href}
                                        href={link.href}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            smoothScrollTo(link.href);
                                        }}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.06 + i * 0.06 }}
                                        className="block py-4 text-2xl font-semibold tracking-tight text-klareet-TEXT border-b border-klareet-bor"
                                    >
                                        {link.label}
                                    </motion.a>
                                ),
                            )}
                            <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.18 }}
                                className="pt-8"
                            >
                                <Button
                                    size="lg"
                                    onClick={() => {
                                        setOpen(false);
                                        navigate("/survey");
                                    }}
                                    className="w-full"
                                    data-testid="nav-take-survey-mobile"
                                >
                                    Take the Survey
                                    <ArrowRight size={18} />
                                </Button>
                            </motion.div>
                        </div>
                    </motion.div>
                ) : null}
            </AnimatePresence>
        </>
    );
}
